/**
 * Welche Fenster vor dem Schließen nachfragen wollen (#850).
 *
 * Eine App meldet über `registerCloseGuard` an, dass sie gefragt werden will —
 * typischerweise, weil ungespeicherte Eingaben im Fenster stehen. Diese Datei
 * hält die eine Regel, die dabei falsch laufen kann, getrennt von der
 * Verdrahtung im Fenster-Context.
 *
 * Die Regel: Ein Guard darf ein Fenster NIE unschließbar machen. Er sagt
 * „frag nach", nicht „nein". Wirft er eine Ausnahme, gilt das Fenster als
 * schließbar — ein kaputter Guard kostet dann im schlimmsten Fall eine
 * Rückfrage, die ausbleibt. Andersherum säße die Person in einem Fenster fest,
 * das sich nicht mehr schließen lässt, und das ist der schlimmere Fehler.
 */

/**
 * Antwort einer App auf die Frage, ob ihr Fenster geschlossen werden darf.
 *
 * `'ask'` heißt nicht „nicht schließen", sondern „frag die Person zuerst".
 * Die Entscheidung trifft immer der Mensch.
 */
export type CloseGuardVerdict = 'close' | 'ask'
export type CloseGuard = () => CloseGuardVerdict

/**
 * Die Teilmenge von `ids`, deren Fenster eine Rückfrage verlangen.
 *
 * Reihenfolge und Duplikate von `ids` bleiben erhalten — der Aufrufer bestimmt,
 * was er damit macht.
 */
export function guardedWindowIds(
  guards: ReadonlyMap<string, CloseGuard>,
  ids: readonly string[],
): string[] {
  return ids.filter((id) => {
    const guard = guards.get(id)
    if (!guard) return false
    try {
      return guard() === 'ask'
    } catch {
      return false
    }
  })
}
